"use client";
import { useEffect, useState } from "react";

export type TocItem = { id: string; label: string };

/** Sticky table of contents with reading progress (wide screens) and a thin progress bar under the
 * header (narrow screens). Server-rendered as plain links, so it works without JavaScript; the script
 * only marks the current section and fills the progress bar. `root` is the id of the article element. */
function useReading(items: TocItem[], root: string) {
  const [active, setActive] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const article = document.getElementById(root);
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const span = Math.max(rect.height - window.innerHeight * 0.6, 1);
      setProgress(Math.min(1, Math.max(0, -rect.top / span)));
      const line = window.innerHeight * 0.25;
      let current: string | null = null;
      for (const item of items) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= line) current = item.id;
      }
      setActive(current);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => { window.removeEventListener("scroll", schedule); window.removeEventListener("resize", schedule); if (frame) cancelAnimationFrame(frame); };
  }, [items, root]);
  return { active, progress };
}

const NO_ITEMS: TocItem[] = [];

/** Thin bar under the header on narrow screens (hidden where the sticky table of contents shows). */
export function ReadingProgress({ root }: { root: string }) {
  const { progress } = useReading(NO_ITEMS, root);
  return <div className="vc-progress-top" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>;
}

export function ReadingToc({ items, label, root }: { items: TocItem[]; label: string; root: string }) {
  const { active, progress } = useReading(items, root);
  const bar = { transform: `scaleX(${progress})` };
  return <nav className="vc-toc" aria-label={label}>
      <span className="ui-eyebrow">{label}</span>
      <div className="vc-toc-progress" aria-hidden="true"><span style={bar} /></div>
      <ol>{items.map(item => <li key={item.id}><a href={`#${item.id}`} aria-current={active === item.id ? "true" : undefined}>{item.label}</a></li>)}</ol>
  </nav>;
}
